// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useSessionTimeout from "@bg/App/Hooks/useSessionTimeout";
import { xIsAuthenticated } from "@bg/App/Store/Selectors/GeneralSelectors";
import Button from "@bg/MobiusOverrides/Button/Button";
import xScreen from "@bg/XDL/Utilities/xScreen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    siteTimeoutMinutes: (getSettingsCollection(state).customSettings as any).siteTimeoutMinutes,
    siteTimeoutNotifyMinutes: (getSettingsCollection(state).customSettings as any).siteTimeoutNotifyMinutes,
});

export interface SessionTimeoutModalStyles {
    modal?: ModalPresentationProps;
    buttonsWrapper?: InjectableCss;
}

export const sessionTimeoutModalStyles: SessionTimeoutModalStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            margin-top: 20px;
        `,
    },
    modal: {
        cssOverrides: {
            modalContainer: css`
                ${xScreen("lte","sm")} {
                    height: auto;
                }
            `,
        },
    },
};


const SessionTimeoutModal = ({
    isAuthenticated,
    siteTimeoutMinutes,
    siteTimeoutNotifyMinutes,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    if (!isAuthenticated) {
        return null;
    }

    // Added + 0.1 so it adds extra 6 seconds to make sure that session got destroyed on BE
    useSessionTimeout(+siteTimeoutMinutes + 0.1, siteTimeoutNotifyMinutes, () => {
        redirectToHomePage();
    }, () => {
        setModalIsOpen(true);
    });

    const redirectToHomePage = () => {
        window.location.href = window.location.origin;
    };

    return (
        <Modal
            {...sessionTimeoutModalStyles.modal}
            headline={siteMessage("Custom_Session_Timeout_Modal_Title")}
            isOpen={modalIsOpen}
            handleClose={() => setModalIsOpen(false)}
            sizeVariant="small"
        >
            <Typography>{siteMessage("Custom_Session_Timeout_Modal_Message")}</Typography>
            <StyledWrapper {...sessionTimeoutModalStyles.buttonsWrapper}>
                <Button
                    onClick={() => setModalIsOpen(false)}
                    variant="secondary"
                    sizeVariant="medium"
                    aria-label="Close"
                >
                    {translate("Close")}
                </Button>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps)(SessionTimeoutModal);